import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import uuid from 'uuid';
import { RowItem, EndOfTable } from './components';
import { differenceInArrayValues } from './helpers';
import { SkeletonText } from '../Skeleton';
import { NoData } from '../NoData';
import { useEffectAfterMount, range, usePrevious } from '../../utils';
export const Table = ({ loading, rowHeaders, data, columns, gridID, onSelectRow, selectedData, columnTypes, hasEnded, languageData, className, showDashIfEmpty = true, numSkeletonRows = 20, skeletonWidths = [], showFooter = false, footerData = {}, footerIds = [], onRowClick, clickedRow, showHeader = true, }) => {
    const headersAvailable = columns && Object.keys(columns).length;
    const selectionDisabled = !!(loading && headersAvailable);
    const [selectedRows, setSelectedRows] = useState([]);
    const [lastSelectedRow, setLastSelectedRow] = useState(-1);
    const [rows, setRows] = useState();
    const [footerRow, setFooterRow] = useState();
    // Reference to the latest values for the functions passed down as props
    const latestSelectedRows = useRef(selectedRows);
    const latestLastSelectedRow = useRef(lastSelectedRow);
    const previousSelectedData = usePrevious(selectedData);
    const createRow = (i, isSelected) => (React.createElement(RowItem, { key: i, rowID: i, items: data[i], columns: columns, gridID: gridID, onRowSelect: onRowSelection, canSelectRow: canSelectRow, isSelected: isSelected, selectionDisabled: false, columnTypes: columnTypes, className: cx('table-row_item table-row_item--hover', {
            'highlight-row': i === clickedRow,
        }), showDashIfEmpty: showDashIfEmpty, onRowClick: onRowClick }));
    useEffectAfterMount(() => {
        latestLastSelectedRow.current = lastSelectedRow;
    }, [lastSelectedRow]);
    useEffectAfterMount(() => {
        if (onSelectRow) {
            onSelectRow(selectedRows);
        }
        latestSelectedRows.current = selectedRows;
    }, [selectedRows]);
    useEffectAfterMount(() => {
        if (data && selectedData) {
            if (selectedData.length === 0 && selectedRows.length > 0) {
                setSelectedRows([]);
                setLastSelectedRow(-1);
            }
            if (rows) {
                const removedRows = differenceInArrayValues(previousSelectedData, selectedData);
                const addedRows = differenceInArrayValues(selectedData, previousSelectedData);
                const newRows = [...rows];
                addedRows.map((i) => {
                    newRows[i] = createRow(i, true);
                });
                removedRows.map((i) => {
                    newRows[i] = createRow(i, false);
                });
                setRows(newRows);
                latestSelectedRows.current = selectedData;
            }
        }
    }, [selectedData]);
    useEffect(() => {
        if (!loading && data && data.length) {
            const dataRows = [];
            data.map((item, i) => {
                // If we have pre-existing rows, no need to create a new row unless the data has actually updated.
                if (rows) {
                    const matchingRow = rows.find((element) => element.props.items.tableId === item.tableId);
                    if (matchingRow) {
                        dataRows.push(matchingRow);
                        return;
                    }
                }
                const isSelected = selectedData ? selectedData.includes(i) : false;
                dataRows.push(React.createElement(RowItem, { key: i, rowID: i, items: item, columns: columns, gridID: gridID, onRowSelect: onRowSelection, canSelectRow: canSelectRow, selectionDisabled: false, columnTypes: columnTypes, isSelected: isSelected, className: cx('table-row_item table-row_item--hover', {
                        'highlight-row': i === clickedRow,
                    }), showDashIfEmpty: showDashIfEmpty, onRowClick: onRowClick }));
            });
            setRows(dataRows);
        }
        if (showFooter) {
            const footerRowItem = (React.createElement(RowItem, { key: uuid.v4(), items: footerData, columns: footerIds, gridID: gridID, rowID: 1, canSelectRow: false, selectionDisabled: false, columnTypes: columnTypes, isSelected: false, className: "table-row_item table-row_item--hover", showDashIfEmpty: showDashIfEmpty }));
            setFooterRow(footerRowItem);
        }
    }, [data, loading]);
    if (loading && !headersAvailable) {
        return React.createElement("div", null, "Loading");
    }
    if (!columns || !headersAvailable || !rowHeaders) {
        return null;
    }
    const onRowSelection = (shiftKey, id) => {
        if (onSelectRow === undefined) {
            return;
        }
        const indexes = [latestLastSelectedRow.current, id].sort((x, y) => x - y);
        // https://stackoverflow.com/a/36963945
        const end = indexes[1] + 1;
        const start = indexes[0];
        const list = [];
        if (shiftKey) {
            list.push(...range(start, end));
        }
        else {
            list.push(id);
        }
        if (latestSelectedRows.current.indexOf(id) !== -1) {
            const filteredRows = latestSelectedRows.current.filter((item) => !list.includes(item));
            setSelectedRows(filteredRows);
        }
        else {
            if (shiftKey) {
                setSelectedRows([
                    ...latestSelectedRows.current,
                    ...range(start, end).filter(item => !latestSelectedRows.current.includes(item)),
                ]);
                if (latestLastSelectedRow.current === -1) {
                    setSelectedRows([...latestSelectedRows.current, id]);
                }
            }
            else {
                setSelectedRows([...latestSelectedRows.current, id]);
            }
        }
        setLastSelectedRow(id);
    };
    const onHeaderSelect = () => {
        if (onSelectRow) {
            if (selectedRows.length > 0) {
                setSelectedRows([]);
                setLastSelectedRow(-1);
            }
            else {
                setSelectedRows([...range(0, data.length)]);
            }
        }
    };
    const isGridSelected = () => {
        if (selectedRows && selectedRows.length && data) {
            if (data.length === selectedRows.length) {
                return true;
            }
            else {
                return 'indeterminate';
            }
        }
        return false;
    };
    const canSelectRow = onSelectRow !== undefined;
    const rowsToDisplay = data && data.length ? rows : React.createElement(React.Fragment, null);
    return (React.createElement("div", { className: "table_wrapper" },
        React.createElement("table", { className: cx('table', className, { 'table--loading': loading }), id: gridID },
            !showFooter && showHeader && (React.createElement("thead", { className: "table_head" },
                React.createElement(RowItem, { items: rowHeaders, columns: columns, gridID: gridID, onRowSelect: onHeaderSelect, canSelectRow: canSelectRow, isSelected: isGridSelected(), selectionDisabled: selectionDisabled, columnTypes: columnTypes, className: "table-row_header" }))),
            React.createElement("tbody", { className: "table_body" },
                rowsToDisplay,
                loading &&
                    headersAvailable &&
                    renderSkeleton(columns, gridID, canSelectRow, numSkeletonRows, skeletonWidths, columnTypes, onRowSelection)),
            showFooter && React.createElement("tfoot", { className: "table_footer" }, footerRow)),
        !loading && data && !data.length && languageData && (React.createElement(NoData, { title: languageData.noDataTitle, text: languageData.noDataText, bordered: true })),
        languageData && typeof hasEnded === 'boolean' && (React.createElement(EndOfTable, { hasEnded: hasEnded }, languageData.endTable))));
};
const renderSkeleton = (columns, gridID, canSelectRow, numberOfRows, skeletonWidths, columnTypes, onSelectRow) => {
    const emptyObjects = new Array();
    for (let i = 1; i <= numberOfRows; i++) {
        const row = {};
        Object.keys(columns).forEach((key, index) => {
            let width = 64;
            if (skeletonWidths && skeletonWidths.length > index) {
                width = skeletonWidths[index];
            }
            row[columns[key]] = (React.createElement(SkeletonText, { key: `dummy-${i}-${key}`, loading: true, width: `${width}px` }));
        });
        emptyObjects.push(row);
    }
    return emptyObjects.map((item, i) => (React.createElement(RowItem, { key: i, rowID: i, items: item, columns: columns, gridID: gridID, onRowSelect: onSelectRow, canSelectRow: canSelectRow, selectionDisabled: true, columnTypes: columnTypes, isSelected: false, className: "table-row_item" })));
};
//# sourceMappingURL=Table.js.map